import type { IconName } from "@/components/SVGIcon/SVGIcon.types";
import type { AccordionItem } from "@/components/SidebarAccordion/SidebarAccordion";
import type {
  PhotoGalleryItem,
  RaportHSPSpecsData,
  TabDataItem,
} from "@mstys/raport-auta-client-front/components";
import type { RaportHSPHistoryData } from "@/components/RaportHSP/RaportHSPTimeline/RaportHSPTimeline";
import type { VehicleIdentificationData } from "@/components/RaportHSP/RaportHSPVehicleIdentification/RaportHSPVehicleIdentification";
import {
  createOptionalEquipmentData,
  createSpecsData,
  createStandardEquipmentData,
  createVehicleIdentificationData,
} from "./api/mapSpecs";
import { createTimelineHistoryData } from "./api/mapTimeline";
import {
  createWarningsData,
  type MappedWarning,
} from "./api/mapWarningsRecommendations";
import type {
  ApiResponseRaportV2,
  EntryV2,
  Evaluation,
  Photos,
  ReportStatsV2,
  TheftDatabasesInfo,
  VehicleTimeline,
} from "./api/report";

const VEHICLE_PARAMETER_FILL_COLOR = "#A4B2C0";

export interface VehicleParameterDisplayItem {
  label: string;
  value: string;
  icon: IconName;
}

export interface SummarySectionData {
  visible: boolean;
  stats?: ReportStatsV2;
  evaluation?: Evaluation;
}

export interface IdentificationSectionData {
  visible: boolean;
  data: VehicleIdentificationData;
}

export interface WarningsSectionData {
  visible: boolean;
  items: MappedWarning[];
}

export interface HistorySectionData {
  visible: boolean;
  /**
   * Sekcja historii szkód jest widoczna, ale pojazd nie ma odnotowanych szkód
   * (dodatek HSP_VEHICLE_HISTORY zwrócił status NOT_FOUND). Zamiast listy szkód
   * renderujemy wtedy kartę "Nie wykazano historii szkodowej".
   */
  noDamage: boolean;
  entries: EntryV2[];
}

export interface TimelineSectionData {
  visible: boolean;
  data: RaportHSPHistoryData;
}

export interface MileageSectionData {
  visible: boolean;
  entries: EntryV2[];
}

export interface SpecsSectionData {
  visible: boolean;
  data?: RaportHSPSpecsData;
  parts: Record<string, boolean>;
}

export interface EquipmentSectionData {
  visible: boolean;
  parameterItems: VehicleParameterDisplayItem[];
  hasFinancialSpecs: boolean;
  hasStandardDetails: boolean;
  evaluation?: Evaluation;
  equipment?: ApiResponseRaportV2["content"]["equipment"];
  standardTabs: TabDataItem[];
  optionalTabs: TabDataItem[];
}

export interface LegalSectionData {
  visible: boolean;
  theftInfo?: TheftDatabasesInfo;
}

export interface EstimationSectionData {
  visible: boolean;
  evaluation?: Evaluation;
}

export interface PhotosSectionData {
  visible: boolean;
  items: PhotoGalleryItem[];
}

export interface ReportV2Sections {
  summary: SummarySectionData;
  identification: IdentificationSectionData;
  warnings: WarningsSectionData;
  history: HistorySectionData;
  timeline: TimelineSectionData;
  mileage: MileageSectionData;
  specs: SpecsSectionData;
  equipment: EquipmentSectionData;
  legal: LegalSectionData;
  estimation: EstimationSectionData;
  photos: PhotosSectionData;
}

export interface ReportHeaderData {
  brandAndModel: string;
  vin: string;
}

export interface ReportV2PreparedData {
  header: ReportHeaderData;
  sections: ReportV2Sections;
  sidebarItems: AccordionItem[];
}

type TranslationFunction = (key: string, defaultValue?: string) => string;

const DEFAULT_HEADER_VALUES: ReportHeaderData = {
  brandAndModel: "Nieznana marka",
  vin: "Brak VIN",
};

const SPEC_SECTION_KEYS: Array<keyof RaportHSPSpecsData | "other"> = [
  "engine",
  "driveTrain",
  "body",
  "dimensions",
  "wheelsSuspension",
  "fuelEmission",
  "other",
];

const hasNonEmptyObject = (data: unknown): boolean => {
  if (!data || typeof data !== "object") {
    return false;
  }

  return Object.values(data as Record<string, unknown>).some((value) => {
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    if (typeof value === "number") {
      return !Number.isNaN(value);
    }
    return Boolean(value);
  });
};

const mapPhotosToItems = (
  photos?: Photos,
  t?: TranslationFunction
): PhotoGalleryItem[] => {
  if (!photos?.claims) {
    return [];
  }

  const seenUrls = new Set<string>();
  const items: PhotoGalleryItem[] = [];

  Object.entries(photos.claims).forEach(([claimId, photoPaths]) => {
    const filteredPaths = (photoPaths ?? []).filter(
      (photoPath): photoPath is string => Boolean(photoPath)
    );

    let photoIndex = 0;
    filteredPaths.forEach((photoPath) => {
      // Normalizuj ścieżkę - usuń duplikaty claimId w ścieżce jeśli występują
      let normalizedPath = photoPath.trim();

      // Usuń duplikaty claimId na początku ścieżki
      const duplicatePattern = new RegExp(`^${claimId}/+${claimId}/+`);
      if (duplicatePattern.test(normalizedPath)) {
        normalizedPath = normalizedPath.replace(
          duplicatePattern,
          `${claimId}/`
        );
      }

      // Upewnij się, że ścieżka zaczyna się od claimId
      if (!normalizedPath.startsWith(`${claimId}/`)) {
        normalizedPath = `${claimId}/${normalizedPath}`;
      }

      const fullUrl = `${photos.baseUrl}${normalizedPath}`;

      // Pomijamy duplikaty na podstawie pełnego URL
      if (!seenUrls.has(fullUrl)) {
        seenUrls.add(fullUrl);
        photoIndex++;
        items.push({
          src: fullUrl,
          alt: t
            ? `${t(
              "photos_labels.damage_photo",
              "Zdjęcie szkody"
            )} ${claimId} - ${photoIndex}`
            : `Zdjęcie szkody ${claimId} - ${photoIndex}`,
          title: t
            ? `${t(
              "photos_labels.damage_from_date",
              "Szkoda z dnia"
            )} ${claimId}`
            : `Szkoda z dnia ${claimId}`,
          width: 800,
          height: 600,
        });
      }
    });
  });

  return items;
};

const buildVehicleParameterItems = (
  vehicleIdentification: VehicleIdentificationData,
  brandModelValue: string,
  t?: TranslationFunction
): VehicleParameterDisplayItem[] => {
  const items: VehicleParameterDisplayItem[] = [];

  if (brandModelValue) {
    items.push({
      label: t
        ? t("vehicle_params.brand_and_model", "Marka i model")
        : "Marka i model",
      value: brandModelValue,
      icon: "people-group",
    });
  }

  if (vehicleIdentification.productionYear) {
    items.push({
      label: t
        ? t("vehicle_params.production_year", "Rok produkcji")
        : "Rok produkcji",
      value: vehicleIdentification.productionYear,
      icon: "calendar-date",
    });
  }

  if (vehicleIdentification.countryOfOrigin) {
    items.push({
      label: t
        ? t("vehicle_params.country_of_origin", "Kraj pochodzenia")
        : "Kraj pochodzenia",
      value: vehicleIdentification.countryOfOrigin,
      icon: "location-pin",
    });
  }

  if (vehicleIdentification.version) {
    items.push({
      label: t
        ? t("vehicle_params.body_version", "Wersja nadwozia")
        : "Wersja nadwozia",
      value: vehicleIdentification.version,
      icon: "car-2",
    });
  }

  return items;
};

const filterHistoryEntries = (entries: EntryV2[]): EntryV2[] =>
  entries.filter(
    (entry) =>
      typeof entry.damageValueRange === "string" &&
      entry.damageValueRange.includes(" - ")
  );

const filterMileageEntries = (
  entries: EntryV2[],
  vehicleTimeline?: VehicleTimeline
): EntryV2[] => {
  // Filtruj wpisy z entries, które mają mileage
  const entriesWithMileage = entries.filter(
    (entry) => typeof entry.mileage === "number" && entry.mileage > 0
  );

  // Stwórz Set z ID wpisów z entries dla szybkiego sprawdzania duplikatów
  const existingIds = new Set(
    entriesWithMileage.map((entry) => entry.id).filter(Boolean)
  );

  // Konwertuj wpisy z vehicleTimeline, które mają mileage
  const timelineEntries: EntryV2[] = [];
  if (vehicleTimeline?.entries) {
    for (const timelineEntry of vehicleTimeline.entries) {
      if (
        typeof timelineEntry.mileage === "number" &&
        timelineEntry.mileage > 0
      ) {
        // Sprawdź czy wpis o tym claimId już istnieje w entries
        // Pomijamy wpis z timeline jeśli już został dodany z entries
        if (
          (timelineEntry.claimId || timelineEntry.details?.claimId) &&
          existingIds.has(timelineEntry.details?.claimId as string)
        ) {
          continue;
        }

        // Formatuj datę do formatu DD.MM.YYYY
        let displayDate = timelineEntry.date;
        try {
          const date = new Date(timelineEntry.date);
          if (!isNaN(date.getTime())) {
            displayDate = date.toLocaleDateString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
          }
        } catch (e) {
          console.error(e);
          // Jeśli parsowanie się nie powiodło, użyj oryginalnej daty
        }

        // Tworzymy minimalny obiekt EntryV2 z wymaganymi polami
        const entryV2: EntryV2 = {
          creationDate: timelineEntry.date,
          displayDate: displayDate,
          countryCode: "",
          countryName: "",
          mileage: timelineEntry.mileage,
          damageValueRange: "",
          damageGroups: [],
          damagePositions: [],
          carModel: "",
          carManufacturer: "",
          damagesNames: [],
          damages: {
            glass: {
              rear: false,
              roof: false,
              front: false,
              sideLeft: false,
            },
            general: {
              rear: false,
              roof: false,
              front: false,
              sideLeft: false,
            },
          },
          damagesOnImage: [],
          currencyIsoCode: "PLN",
          currencyName: "Polski złoty",
          currency: "PLN",
          id: timelineEntry.claimId || `timeline-${timelineEntry.date}`,
          claimId: timelineEntry.claimId,
        };
        timelineEntries.push(entryV2);
      }
    }
  }

  // Łączymy oba źródła danych
  const allEntries = [...entriesWithMileage, ...timelineEntries];
  // Sortuj po dacie (najstarsze pierwsze)
  return allEntries.sort((a, b) => {
    const dateA = new Date(a.creationDate || a.displayDate).getTime();
    const dateB = new Date(b.creationDate || b.displayDate).getTime();
    return dateB - dateA;
  });
};

const buildSpecPartsVisibility = (
  specsData: Partial<RaportHSPSpecsData> | undefined
): Record<string, boolean> => {
  return SPEC_SECTION_KEYS.reduce<Record<string, boolean>>(
    (visibilityMap, sectionKey) => {
      const sectionData = specsData?.[sectionKey as keyof RaportHSPSpecsData];
      visibilityMap[sectionKey] = hasNonEmptyObject(
        sectionData as Record<string, unknown> | undefined
      );
      return visibilityMap;
    },
    {}
  );
};

const buildSidebarItems = (
  sections: ReportV2Sections,
  specPartsVisibility: Record<string, boolean>,
  t?: TranslationFunction
): AccordionItem[] => {
  const items: AccordionItem[] = [
    // {
    //   label: t
    //     ? t("sidebar.key_indicators", "Kluczowe wskaźniki")
    //     : "Raport historii szkód",
    //   value: "hsp-podsumowanie",
    //   elementId: "hsp-podsumowanie",
    //   icon: "paper",
    //   active: true,
    // },
  ];

  // if (sections.warnings.visible) {
  //   items.push({
  //     label: t
  //       ? t("sidebar.recommendations_warnings", "Rekomendacje i ostrzeżenia")
  //       : "Rekomendacje i ostrzeżenia",
  //     value: "rekomendacje-ostrzezenia",
  //     elementId: "rekomendacje-ostrzezenia",
  //     icon: "warning",
  //   });
  // }

  if (sections.identification.visible) {
    items.push({
      label: t
        ? t("sidebar.identification_data", "Dane identyfikacyjne")
        : "Dane identyfikacyjne",
      value: "dane-identyfikacyjne",
      elementId: "dane-identyfikacyjne",
      icon: "i",
    });
  }

  if (sections.timeline.visible) {
    items.push({
      label: t
        ? t("sidebar.vehicle_timeline", "Oś czasu pojazdu")
        : "Oś czasu pojazdu",
      value: "os-czasu",
      elementId: "os-czasu",
      icon: "fish-swimming",
    });
  }

  if (sections.mileage.visible) {
    items.push({
      label: t
        ? t("sidebar.mileage_chart", "Wykres przebiegów")
        : "Wykres przebiegów",
      value: "historia-przebiegu",
      elementId: "historia-przebiegu",
      icon: "chart-analytics",
    });
  }

  if (sections.history.visible) {
    items.push({
      label: t
        ? t("sidebar.damage_history", "Historia szkód pojazdu")
        : "Historia szkód pojazdu",
      value: "historia-szkod",
      elementId: "historia-szkod",
      icon: "car-accident",
    });
  }

  if (sections.legal.visible) {
    items.push({
      label: t ? t("sidebar.legal_status", "Status prawny") : "Status prawny",
      value: "status-prawny",
      elementId: "status-prawny",
      icon: "libra",
    });
  }

  if (sections.estimation.visible) {
    items.push({
      label: t
        ? t("sidebar.vehicle_valuation", "Wycena wartości pojazdu")
        : "Wycena wartości pojazdu",
      value: "wycena",
      elementId: "wycena",
      icon: "ruler-measure",
    });
  }

  if (sections.equipment.visible) {
    const equipmentSubItems: AccordionItem["items"] = [];

    const hasStandardDetails =
      sections.equipment.parameterItems.length > 0 ||
      sections.equipment.hasFinancialSpecs ||
      sections.equipment.standardTabs.length > 0;

    if (hasStandardDetails) {
      equipmentSubItems?.push({
        label: t
          ? t("sidebar.standard_equipment", "Wyposażenie standardowe")
          : "Wyposażenie standardowe",
        elementId: "wyposazenie-standardowe",
        icon: "handshake",
      });
    }

    if (sections.equipment.optionalTabs.length > 0) {
      equipmentSubItems?.push({
        label: t
          ? t("sidebar.optional_equipment", "Wyposażenie opcjonalne")
          : "Wyposażenie opcjonalne",
        elementId: "wyposazenie-opcjonalne",
        icon: "plus-circle",
      });
    }

    items.push({
      label: t
        ? t("sidebar.vehicle_equipment", "Wyposażenie pojazdu")
        : "Wyposażenie pojazdu",
      value: "wyposazenie-pojazdu",
      elementId: "wyposazenie-pojazdu",
      icon: "trophy",
      ...(equipmentSubItems && equipmentSubItems.length > 0
        ? { items: equipmentSubItems }
        : {}),
    });
  }

  if (sections.specs.visible) {
    const specSubItems: AccordionItem["items"] = [];

    if (specPartsVisibility.engine) {
      specSubItems?.push({
        label: t
          ? t("sidebar.engine_and_performance", "Silnik i osiągi")
          : "Silnik i osiągi",
        elementId: "dane-techniczne-silnik-i-osiagi",
        icon: "handshake",
      });
    }
    if (specPartsVisibility.driveTrain) {
      specSubItems?.push({
        label: t ? t("sidebar.drivetrain", "Układ napędowy") : "Układ napędowy",
        elementId: "dane-techniczne-uklad-napedowy",
        icon: "network-nodes",
      });
    }
    if (specPartsVisibility.body) {
      specSubItems?.push({
        label: t ? t("sidebar.body", "Nadwozie") : "Nadwozie",
        elementId: "dane-techniczne-nadwozie",
        icon: "car",
      });
    }
    if (specPartsVisibility.dimensions) {
      specSubItems?.push({
        label: t
          ? t("sidebar.dimensions_and_weight", "Wymiary i masa")
          : "Wymiary i masa",
        elementId: "dane-techniczne-wymiary-i-masa",
        icon: "person-shirt",
      });
    }
    if (specPartsVisibility.wheelsSuspension) {
      specSubItems?.push({
        label: t
          ? t("sidebar.wheels_and_suspension", "Koła i zawieszenie")
          : "Koła i zawieszenie",
        elementId: "dane-techniczne-koła-i-zawieszenie",
        icon: "network-connection",
      });
    }
    if (specPartsVisibility.fuelEmission) {
      specSubItems?.push({
        label: t
          ? t("sidebar.fuel_and_emissions", "Zużycie paliwa i emisje")
          : "Zużycie paliwa i emisje",
        elementId: "dane-techniczne-zużycie-paliwa-i-emisje",
        icon: "gas",
      });
    }
    if (specPartsVisibility.other) {
      specSubItems?.push({
        label: t
          ? t("sidebar.other_info", "Inne informacje")
          : "Inne informacje",
        elementId: "dane-techniczne-inne-informacje",
        icon: "i",
      });
    }

    items.push({
      label: t
        ? t("sidebar.technical_data", "Dane techniczne")
        : "Dane techniczne",
      value: "dane-techniczne",
      elementId: "dane-techniczne",
      icon: "car",
      ...(specSubItems && specSubItems.length > 0
        ? { items: specSubItems }
        : {}),
    });
  }

  if (sections.photos.visible) {
    items.push({
      label: t ? t("sidebar.photo_gallery", "Galeria zdjęć") : "Galeria zdjęć",
      value: "galeria-zdjec",
      elementId: "galeria-zdjec",
      icon: "grid-squares",
    });
  }

  return items;
};

/**
 * Buduje dane nagłówka raportu
 * Priorytet dla VIN: technicalParameters.vehicleIdentification.vin -> basicCarInformationDto.vin -> fallback
 * Priorytet dla marki i modelu: technicalParameters.vehicleIdentification.brandAndModel -> basicCarInformationDto (brand + model) -> fallback
 */
const buildHeaderData = (
  raport: ApiResponseRaportV2,
  vehicleIdentificationData: VehicleIdentificationData,
  t?: TranslationFunction
): ReportHeaderData => {
  const basicCarInfo = raport.content.basicCarInformationDto;
  const fallbackBrandModel = basicCarInfo
    ? [basicCarInfo.brand, basicCarInfo.model]
      .filter((value) => Boolean(value?.trim()))
      .join(" ")
    : "";

  const defaultBrandModel = t
    ? t("defaults.unknown_brand", DEFAULT_HEADER_VALUES.brandAndModel)
    : DEFAULT_HEADER_VALUES.brandAndModel;
  const defaultVin = t
    ? t("defaults.no_vin", DEFAULT_HEADER_VALUES.vin)
    : DEFAULT_HEADER_VALUES.vin;

  return {
    brandAndModel:
      raport.content.technicalParameters?.vehicleIdentification?.brandAndModel?.trim() ||
      fallbackBrandModel ||
      defaultBrandModel,
    vin:
      raport.content.technicalParameters?.vehicleIdentification?.vin?.trim() ??
      basicCarInfo?.vin?.trim() ??
      defaultVin,
  };
};

export interface PrepareReportV2Options {
  /**
   * Dodatek HSP_VEHICLE_HISTORY zwrócił status NOT_FOUND — pojazd został
   * sprawdzony i nie odnotowano dla niego żadnych szkód w bazach.
   */
  historyNoDamage?: boolean;
}

export const prepareReportV2Data = (
  raport: ApiResponseRaportV2,
  t?: TranslationFunction,
  options?: PrepareReportV2Options
): ReportV2PreparedData => {
  const historyNoDamage = Boolean(options?.historyNoDamage);
  const entries = raport.content.entries ?? [];
  const warnings = createWarningsData(
    raport.content.warningsAndRecommendations,
    t
  );
  const visibleWarnings = warnings.filter(
    (warning) => warning.problemStatus !== "MissingData"
  );
  const vehicleIdentificationData = createVehicleIdentificationData(
    raport.content.technicalParameters?.vehicleIdentification
  );

  // Budowanie brandModelValue z fallbackiem do basicCarInformationDto
  const brandModelValue =
    [vehicleIdentificationData.brand, vehicleIdentificationData.model]
      .filter((value) => Boolean(value))
      .join(" ") ||
    [
      raport.content.basicCarInformationDto?.brand,
      raport.content.basicCarInformationDto?.model,
    ]
      .filter((value) => Boolean(value?.trim()))
      .join(" ");

  const parameterItems = buildVehicleParameterItems(
    vehicleIdentificationData,
    brandModelValue,
    t
  );

  const historyEntries = filterHistoryEntries(entries);
  const mileageEntries = filterMileageEntries(
    entries,
    raport.content.vehicleTimeline
  );
  const timelineData = createTimelineHistoryData(
    raport.content.vehicleTimeline,
    t
  );

  const specsDataRaw = createSpecsData(raport.content.technicalParameters);
  const specPartsVisibility = buildSpecPartsVisibility(
    specsDataRaw as Partial<RaportHSPSpecsData>
  );
  const hasSpecsSection = Object.values(specPartsVisibility).some(
    (isVisible) => isVisible
  );

  const standardTabs = createStandardEquipmentData(raport.content.equipment);
  const optionalTabs = createOptionalEquipmentData(raport.content.equipment);

  const evaluation = raport.content.evaluation;
  const hasFinancialSpecs =
    Boolean(
      evaluation &&
      typeof evaluation.baseCarValue === "number" &&
      !Number.isNaN(evaluation.baseCarValue)
    ) || false;

  // Sekcja Equipment jest widoczna tylko gdy są faktyczne dane o wyposażeniu lub wycenie
  const hasEquipmentData = Boolean(raport.content.equipment);
  const hasEquipmentStandardSection =
    hasEquipmentData &&
    (hasFinancialSpecs || standardTabs.length > 0);

  const photosItems = mapPhotosToItems(raport.content.photos, t);
  const sections: ReportV2Sections = {
    summary: {
      visible: Boolean(raport.content.reportStats),
      stats: raport.content.reportStats,
      evaluation,
    },
    identification: {
      // Dane identyfikacyjne z wyceny RA2 mogą nie odpowiadać faktycznemu pojazdowi.
      visible: false,
      data: vehicleIdentificationData,
    },
    warnings: {
      visible: visibleWarnings.length > 0,
      items: visibleWarnings,
    },
    history: {
      visible: historyEntries.length > 0 || historyNoDamage,
      noDamage: historyEntries.length === 0 && historyNoDamage,
      entries: historyEntries.map((entry) => ({
        ...entry,
      })),
    },
    timeline: {
      visible: timelineData.timeline.length > 0,
      data: timelineData,
    },
    mileage: {
      visible: mileageEntries.length > 1,
      entries: mileageEntries,
    },
    specs: {
      visible: hasSpecsSection,
      data: hasSpecsSection ? (specsDataRaw as RaportHSPSpecsData) : undefined,
      parts: specPartsVisibility,
    },
    equipment: {
      visible:
        hasEquipmentData &&
        (hasEquipmentStandardSection ||
          optionalTabs.length > 0 ||
          hasFinancialSpecs),
      parameterItems,
      hasFinancialSpecs,
      hasStandardDetails: hasEquipmentStandardSection,
      evaluation,
      equipment: raport.content.equipment,
      standardTabs,
      optionalTabs,
    },
    legal: {
      visible: Boolean(
        raport.content.theftDatabasesInfo &&
        (raport.content.theftDatabasesInfo.checkedAt ||
          (raport.content.theftDatabasesInfo.pledgeRegisters?.length ?? 0) >
          0 ||
          (raport.content.theftDatabasesInfo.stolenDatabases?.length ?? 0) >
          0)
      ),
      theftInfo: raport.content.theftDatabasesInfo,
    },
    estimation: {
      visible: Boolean(
        evaluation &&
        typeof evaluation.currentMarketValue === "number" &&
        typeof evaluation.depreciation?.value === "number" &&
        typeof evaluation.depreciation?.rate === "number"
      ),
      evaluation,
    },
    photos: {
      visible: photosItems.length > 0,
      items: photosItems,
    },
  };

  const sidebarItems = buildSidebarItems(sections, specPartsVisibility, t);

  return {
    header: buildHeaderData(raport, vehicleIdentificationData, t),
    sections,
    sidebarItems,
  };
};

export const getVehicleParameterFillColor = (): string =>
  VEHICLE_PARAMETER_FILL_COLOR;
